import React, { useState, useMemo, useRef, useEffect, useCallback } from "react";
import ReactDOM from "react-dom";
import { MoreHorizontal, RefreshCw } from "lucide-react";
import Table from "../../../components/ACS-Dashboard/ACS-Blocks/Table";
import useExpandedRowHover from "../../../components/ACS-Dashboard/ACS-Blocks/useExpandedRowHover";
import GlobalPagination from "../../../components/ACS-Dashboard/ACS-Blocks/GlobalPagination";
import SearchBox from "../../../components/ACS-Dashboard/ACS-Blocks/SearchBox";
import FilterButton from "../../../components/ACS-Dashboard/ACS-Blocks/FilterButton";
import ActionMenu from "../../../components/ACS-Dashboard/ACS-Blocks/ActionMenu";
import Loader from "../../../components/ACS-Dashboard/ACS-Blocks/Loader";
import notify from "../../../components/ACS-Dashboard/ACS-Blocks/Toast";
import { fetchTrailersGrid } from "./api";
import { mergePendingCheckInTrailers, normalizeShipmentDirection } from "./yardCheckInTrailerGrid";
import { formatShipmentTypeDisplay } from "./yardViewUtils";
import { usePrivilege } from "../../../guard/usePrivilege";

// ─── Column definitions ───────────────────────────────────────────────────────
const TABLE_HEADERS = [
  "Selection",
  "Action",
  "Trailer #",
  "Condition",
  "Location",
  "Carrier",
  "Load Type",
  "Shipment #",
  "Appointment #",
  "Dwell Hours",
  "Checked In / Out",
];

const DATA_COLUMN_MIN_WIDTH    = 180;
const DATA_COLUMN_MAX_WIDTH    = 360;
const SELECTION_COLUMN_WIDTH   = 80;
const ACTION_COLUMN_WIDTH      = 50;
const STICKY_TRAILER_NUM_LEFT  = SELECTION_COLUMN_WIDTH + ACTION_COLUMN_WIDTH; // 130px
const TRAILER_NUMBER_HEADER    = "Trailer #";

function trailerApiId(raw) {
  if (!raw || typeof raw !== "object") return null;
  return raw.id ?? raw.trailerId ?? null;
}

// ─── API enum → display text ──────────────────────────────────────────────────
const STATUS_DISPLAY = {
  LOADED:           "Loaded",
  EMPTY:            "Empty",
  PARTIALLY_EMPTY:  "Partially Empty",
  PARTIALLY_LOADED: "Partially Loaded",
  UNLOADED:         "Unloaded",
};

const GATE_STATUS_DISPLAY = {
  CHECKED_IN:  "Checked In",
  CHECKED_OUT: "Checked Out",
};

const LOAD_TYPE_DISPLAY = {
  DROP_TRAILER: "Drop Trailer",
  LIVE_LOAD:    "Live Load",
};

// ─── Status badge classes ─────────────────────────────────────────────────────
const STATUS_BADGE_CLASS = {
  LOADED:           "bg-green-100 dark:bg-green-900/40 text-black dark:text-green-200",
  EMPTY:            "bg-gray-100 dark:bg-gray-700/40 text-black dark:text-gray-200",
  PARTIALLY_EMPTY:  "bg-amber-100 dark:bg-amber-900/40 text-black dark:text-amber-200",
  PARTIALLY_LOADED: "bg-amber-100 dark:bg-amber-900/40 text-black dark:text-amber-200",
  UNLOADED:         "bg-blue-100 dark:bg-blue-900/40 text-black dark:text-blue-200",
  DEFAULT:          "bg-gray-100 dark:bg-gray-700/40 text-black dark:text-gray-200",
};

const GATE_STATUS_BADGE_CLASS = {
  CHECKED_IN:  "bg-blue-100 dark:bg-blue-900/40 text-black dark:text-blue-200",
  CHECKED_OUT: "bg-slate-100 dark:bg-slate-700/40 text-black dark:text-slate-200",
};

export const STATUS_FILTER_OPTIONS = [
  "All Conditions",
  "Loaded",
  "Empty",
  "Partially Empty",
  "Partially Loaded",
  "Unloaded",
];

// ─── Component ────────────────────────────────────────────────────────────────
const TrailersTab = ({
  onOpenTrailer,
  onOpenCheckout,
  refreshKey,
  onSelectedTrailersChange,
  selectionClearSignal = 0,
  /** When true, filter row is rendered by parent (Shipment-style toolbar). */
  hideToolbar = false,
  externalSearchQuery = "",
  onExternalSearchQueryChange,
  externalSearchTrigger = 0,
  externalStatusFilter = "All Conditions",
  onExternalStatusFilterChange,
  onFilteredCountChange,
}) => {
  const { hasPrivilege } = usePrivilege();
  const canCheckOutTrailer = hasPrivilege("YMS:CHECKOUT_PATCH");
  const [internalSearchQuery, setInternalSearchQuery] = useState("");
  const [internalSearchTrigger, setInternalSearchTrigger] = useState(0);
  const [internalStatusFilter, setInternalStatusFilter] = useState("All Conditions");
  const searchQuery = hideToolbar ? externalSearchQuery : internalSearchQuery;
  const setSearchQuery = hideToolbar ? onExternalSearchQueryChange : setInternalSearchQuery;
  const searchTrigger = hideToolbar ? externalSearchTrigger : internalSearchTrigger;
  const statusFilter = hideToolbar ? externalStatusFilter : internalStatusFilter;
  const setStatusFilter = hideToolbar ? onExternalStatusFilterChange : setInternalStatusFilter;
  const [currentPage, setCurrentPage] = useState(1);
  const [trailersPerPage, setTrailersPerPage] = useState(25);
  const [selectedTrailers, setSelectedTrailers] = useState([]);
  const [openDropdownRow, setOpenDropdownRow] = useState(null);
  const [dropdownPos, setDropdownPos]         = useState({ top: 0, left: 0 });
  const [reloading, setReloading]             = useState(false);
  const [trailers, setTrailers]               = useState([]);
  const [isLoading, setIsLoading]             = useState(false);

  // Hovering any cell wraps the whole row, so long values in every column are readable at once.
  const { expandedRowKey, getRowHoverProps }  = useExpandedRowHover();

  const dropdownRef = useRef(null);

  const loadTrailers = useCallback(async () => {
    setIsLoading(true);
    try {
      const data = await fetchTrailersGrid();
      const list = Array.isArray(data) ? data : [];
      setTrailers(mergePendingCheckInTrailers(list));
    } catch (err) {
      console.error("Failed to fetch trailers:", err);
      setTrailers(mergePendingCheckInTrailers([]));
    } finally {
      setIsLoading(false);
    }
  }, []);

  // Initial load + re-fetch whenever a move succeeds (refreshKey increments)
  useEffect(() => { loadTrailers(); }, [loadTrailers, refreshKey]);

  useEffect(() => {
    const onCheckIn = () => loadTrailers();
    window.addEventListener("yard-trailer-check-in", onCheckIn);
    return () => window.removeEventListener("yard-trailer-check-in", onCheckIn);
  }, [loadTrailers]);

  useEffect(() => {
    if (!onSelectedTrailersChange) return;
    const raw = trailers.filter((t) =>
      selectedTrailers.includes(t.trailerNumber)
    );
    onSelectedTrailersChange(raw);
  }, [trailers, selectedTrailers, onSelectedTrailersChange]);

  useEffect(() => {
    if (selectionClearSignal > 0) setSelectedTrailers([]);
  }, [selectionClearSignal]);

  // ─── Filter ───────────────────────────────────────────────────────────────
  const filteredTrailers = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    return trailers.filter((t) => {
      const displayStatus = STATUS_DISPLAY[t.status] || t.status || "";
      const matchesStatus =
        statusFilter === "All Conditions" || displayStatus === statusFilter;
      const matchesSearch =
        !q ||
        (t.trailerNumber || "").toLowerCase().includes(q) ||
        (t.carrierName   || "").toLowerCase().includes(q) ||
        (t.location      || "").toLowerCase().includes(q) ||
        displayStatus.toLowerCase().includes(q) ||
        (GATE_STATUS_DISPLAY[t.gateStatus] || "").toLowerCase().includes(q) ||
        (formatShipmentTypeDisplay(
          normalizeShipmentDirection(t.shipmentType) ?? t.shipmentType
        ) || "").toLowerCase().includes(q);
      return matchesStatus && matchesSearch;
    });
  }, [trailers, searchTrigger, statusFilter, searchQuery]);

  useEffect(() => {
    setCurrentPage(1);
  }, [statusFilter, searchTrigger]);

  useEffect(() => {
    onFilteredCountChange?.(filteredTrailers.length);
  }, [filteredTrailers, onFilteredCountChange]);

  // ─── Paginate ─────────────────────────────────────────────────────────────
  const totalPages = Math.max(1, Math.ceil(filteredTrailers.length / trailersPerPage));
  const paginatedTrailers = filteredTrailers.slice(
    (currentPage - 1) * trailersPerPage,
    currentPage * trailersPerPage
  );

  // ─── Handlers ─────────────────────────────────────────────────────────────
  const handleSearch = () => {
    setCurrentPage(1);
    if (!hideToolbar) {
      setInternalSearchTrigger((n) => n + 1);
    }
  };

  const handleFilter = (value) => {
    setStatusFilter(value === "All Conditions" ? "All Conditions" : value);
    setCurrentPage(1);
  };

  const handleSelectAll = (checked) => {
    setSelectedTrailers(
      checked ? paginatedTrailers.map((t) => t.trailerNumber) : []
    );
  };

  const handleSingleCheckbox = (trailerNumber) => {
    setSelectedTrailers((prev) =>
      prev.includes(trailerNumber)
        ? prev.filter((n) => n !== trailerNumber)
        : [...prev, trailerNumber]
    );
  };

  const handleActionClick = (trailerNumber, e) => {
    e.stopPropagation();
    if (openDropdownRow === trailerNumber) {
      setOpenDropdownRow(null);
      return;
    }
    const rect = e.currentTarget.getBoundingClientRect();
    setDropdownPos({ top: rect.bottom + window.scrollY, left: rect.left + window.scrollX });
    setOpenDropdownRow(trailerNumber);
  };

  // ─── Format rows for Table ────────────────────────────────────────────────
  const formattedTrailers = paginatedTrailers.map((trailer) => {
    const displayStatus = STATUS_DISPLAY[trailer.status] || trailer.status || "-";
    const displayGateStatus = GATE_STATUS_DISPLAY[trailer.gateStatus] || "-";

    return {
      _trailerNumber: trailer.trailerNumber,
      _raw: trailer,
      [TRAILER_NUMBER_HEADER]: trailer.trailerNumber || "-",
      Condition: (
        <span className={`text-black text-fluid-sm px-fluid-1.5 py-fluid-1 rounded font-medium ${
          STATUS_BADGE_CLASS[trailer.status] || STATUS_BADGE_CLASS.DEFAULT
        }`}>
          {displayStatus}
        </span>
      ),
      Location:        trailer.location                                   || "-",
      Carrier:         trailer.carrierName                                || "-",
      "Load Type": LOAD_TYPE_DISPLAY[trailer.loadType] || trailer.loadType || "-",
      "Shipment #":    trailer.shipmentNumber != null && trailer.shipmentNumber !== ""
        ? String(trailer.shipmentNumber)
        : "-",
      "Appointment #": trailer.appointmentNumber != null && trailer.appointmentNumber !== ""
        ? String(trailer.appointmentNumber)
        : "-",
      "Dwell Hours":   trailer.dwellTime || "-",
      "Checked In / Out": trailer.gateStatus ? (
        <span className={`text-black text-fluid-sm px-fluid-1.5 py-fluid-1 rounded font-medium ${
          GATE_STATUS_BADGE_CLASS[trailer.gateStatus] || STATUS_BADGE_CLASS.DEFAULT
        }`}>
          {displayGateStatus}
        </span>
      ) : "-",
    };
  });

  // ─── Action column renderer ───────────────────────────────────────────────
  const renderActionColumn = (row) => {
    const trailerNumber = row._trailerNumber;
    const isOpen = openDropdownRow === trailerNumber;
    const apiId = trailerApiId(row._raw);
    const canView = apiId != null;
    const canCheckOut =
      canView && canCheckOutTrailer && row._raw?.gateStatus === "CHECKED_IN";
    const actions = [
      {
        icon: "Eye",
        label: "View",
        color: "blue",
        action: () => {
          if (!canView) {
            notify.info({
              title: "Trailer pending",
              message: "Details will be available after the trailer record syncs.",
            });
            return;
          }
          onOpenTrailer(row._raw);
        },
      },
      ...(canCheckOut
        ? [
            {
              icon: "LogOut",
              label: "Check Out",
              color: "green",
              action: () => {
                setOpenDropdownRow(null);
                onOpenCheckout(row._raw);
              },
            },
          ]
        : []),
    ];

    return (
      <>
        <td
          onClick={(e) => e.stopPropagation()}
          className="sticky bg-white dark:bg-acs-dark-surface border-b border-gray-200 dark:border-acs-dark-border text-center transition-none z-[10]"
          style={{
            left:     `${SELECTION_COLUMN_WIDTH}px`,
            width:    `${ACTION_COLUMN_WIDTH}px`,
            minWidth: `${ACTION_COLUMN_WIDTH}px`,
            maxWidth: `${ACTION_COLUMN_WIDTH}px`,
            boxShadow: "2px 0 2px -1px rgba(0,0,0,0.1)",
          }}
        >
          <div className="relative">
            <button
              data-action-trigger
              className="p-1 relative w-full transition-none text-gray-500 dark:text-gray-400 hover:text-gray-800 dark:hover:text-acs-dark-text"
              onClick={(e) => handleActionClick(trailerNumber, e)}
            >
              <MoreHorizontal size={18} className="mx-auto" />
            </button>
          </div>
        </td>

        {isOpen && ReactDOM.createPortal(
          <div
            ref={dropdownRef}
            style={{ position: "absolute", top: dropdownPos.top, left: dropdownPos.left, zIndex: 9999 }}
          >
            <ActionMenu
              rowId={trailerNumber}
              isOpen={isOpen}
              onClose={() => setOpenDropdownRow(null)}
              moduleName="Trailer"
              actions={actions}
            />
          </div>,
          document.body
        )}
      </>
    );
  };

  // ─── Render ───────────────────────────────────────────────────────────────
  return (
    <div className="flex h-full min-h-0 flex-1 flex-col overflow-hidden font-roboto">

      {!hideToolbar && (
      <div className="flex flex-wrap justify-between items-center rounded-md ml-[2] p-fluid-1">
        <div className="flex flex-wrap items-center gap-2">
          <FilterButton
            text={statusFilter}
            options={STATUS_FILTER_OPTIONS}
            selectedValue={statusFilter}
            onSelect={handleFilter}
          />

          <SearchBox
            value={searchQuery}
            onChange={setSearchQuery}
            onSearch={handleSearch}
            placeholder="Search Trailers"
          />

          <span className="text-gray-500 text-fluid-sm font-semibold text-acs-light-text dark:text-acs-dark-text">
            {filteredTrailers.length} Trailers
          </span>

          <button
            onClick={() => { setReloading(true); loadTrailers().finally(() => setReloading(false)); }}
            className="p-1 rounded-full hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-[#223688]/30 transition"
            title="Reload"
          >
            <RefreshCw
              size={16}
              className={`text-gray-500 text-acs-light-text dark:text-acs-dark-text ${reloading ? "animate-spin" : "hover:text-gray-700"}`}
            />
          </button>
        </div>
      </div>
      )}

      {/* Table + Pagination */}
      <div className="flex-1 mt-fluid-2 min-h-0 flex flex-col scrollbar-hide">
        <div className="bg-white dark:bg-acs-dark-surface flex flex-col h-full transition-none relative">

          {/* Loading overlay */}
          {isLoading && (
            <div className="absolute inset-0 z-[2000] flex items-center justify-center bg-white/60 dark:bg-black/30">
              <Loader
                color="text-blue-600 dark:text-acs-dark-text"
                size="h-5 w-5"
                message="Loading Trailers..."
              />
            </div>
          )}

          {/* Table scroll container */}
          <div className="flex-1 min-h-[200px] sm:min-h-[300px] overflow-x-auto bg-white dark:bg-acs-dark-surface transition-none">
            <Table
              headers={TABLE_HEADERS}
              data={formattedTrailers}
              onSelectAll={handleSelectAll}
              selectedRows={selectedTrailers}
              totalValidRows={formattedTrailers.length}
              autoGrowColumns
              dataColumnMinWidth={DATA_COLUMN_MIN_WIDTH}
              dataColumnMaxWidth={DATA_COLUMN_MAX_WIDTH}
              enableColumnResize
              stickyColumnOffsets={{ 2: `${STICKY_TRAILER_NUM_LEFT}px` }}
              hideHeaderSortIcons
            >
              {(row, _rowIdx, columnWidths) => (
                <>
                  {/* Selection column */}
                  <td
                    className="sticky left-0 bg-white dark:bg-acs-dark-surface border-b border-gray-200 dark:border-acs-dark-border z-[10] transition-none"
                    style={{
                      width:    `${SELECTION_COLUMN_WIDTH}px`,
                      minWidth: `${SELECTION_COLUMN_WIDTH}px`,
                      maxWidth: `${SELECTION_COLUMN_WIDTH}px`,
                      boxShadow: "2px 0 2px -1px rgba(0,0,0,0.1)",
                    }}
                  >
                    <input
                      type="checkbox"
                      className="relative appearance-none cursor-pointer mx-auto block w-[15px] h-[15px] rounded-sm border border-gray-400 dark:border-acs-dark-border bg-white dark:bg-acs-dark-surface transition-none checked:bg-acs-checkbox dark:checked:bg-acs-checkbox-dark checked:border-acs-checkbox dark:checked:border-acs-checkbox-dark after:content-[''] after:absolute after:inset-0 after:flex after:items-center after:justify-center checked:after:content-['✓'] checked:after:text-white checked:after:text-[12px] checked:after:font-bold"
                      checked={selectedTrailers.includes(row._trailerNumber)}
                      onChange={() => handleSingleCheckbox(row._trailerNumber)}
                    />
                  </td>

                  {/* Action column */}
                  {renderActionColumn(row)}

                  {/* Data columns */}
                  {TABLE_HEADERS.slice(2).map((header) => {
                    const isTrailerNumberCol = header === TRAILER_NUMBER_HEADER;
                    const isExpanded = expandedRowKey === row._trailerNumber;
                    return (
                      <td
                        key={header}
                        className={`px-2 sm:px-3 py-1 sm:py-2 text-gray-900 dark:text-acs-dark-text text-xs sm:text-sm md:text-base border-b border-gray-200 dark:border-acs-dark-border font-roboto transition-none ${
                          isTrailerNumberCol ? "sticky bg-white dark:bg-acs-dark-surface z-[9]" : ""
                        }`}
                        onDoubleClick={() => onOpenTrailer(row._raw)}
                        {...getRowHoverProps(row._trailerNumber)}
                        style={{
                          left:      isTrailerNumberCol ? `${STICKY_TRAILER_NUM_LEFT}px` : undefined,
                          boxShadow: isTrailerNumberCol ? "2px 0 2px -1px rgba(0,0,0,0.1)" : undefined,
                          whiteSpace: isExpanded ? "normal" : "nowrap",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          // Lets unbroken tokens (IDs, comma-joined numbers) wrap too, not just space-separated text.
                          overflowWrap: "break-word",
                          wordBreak: "break-word",
                          minWidth: `${columnWidths?.[header] || DATA_COLUMN_MIN_WIDTH}px`,
                          maxWidth: isExpanded ? `${DATA_COLUMN_MAX_WIDTH * 2}px` : `${DATA_COLUMN_MAX_WIDTH}px`,
                          width: columnWidths?.[header] ? `${columnWidths[header]}px` : `${DATA_COLUMN_MIN_WIDTH}px`,
                          transition: "max-width 120ms ease",
                        }}
                      >
                        {row[header]}
                      </td>
                    );
                  })}
                </>
              )}
            </Table>
          </div>

          {/* Pagination */}
          <GlobalPagination
            label="Trailers per page"
            currentPage={currentPage}
            totalPages={totalPages}
            onPageChange={setCurrentPage}
            itemsPerPage={trailersPerPage}
            onItemsPerPageChange={(n) => { setTrailersPerPage(n); setCurrentPage(1); }}
            options={[10, 25, 50]}
          />
        </div>
      </div>

    </div>
  );
};

export default TrailersTab;
